import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { IconButton, Utils, DateTools, Tooltip } from '@treezor/design-system';

import { selectProfileCardByUserIdAndCardId } from '@/store/selectors';
import { useProfileContext } from '@/features/context/ProfileContext';
import { RenewalBlock } from '@/features/cards/components/CardDetails/RenewalBlock';
import { InformationBlock } from '@/features/cards/components/CardUI/InformationBlock';
import { FullWidthContent } from '@/features/cards/components/CardUI/FullWidthContent';
import { SendToParent } from '@/features/cards/components/SendToParent';
import { PaymentCardStatus } from '@/features/cards/components/CardDetails/PaymentCardStatus';

import { useAppSelector } from '@/hooks/redux-helpers';

import { IPaymentCard } from '@/features/cards/store/models/card.model';

import { HeaderContainer } from '@/ui-kit/table/HeaderContainer';
import { Container } from '@/ui-kit/table/Container';

type TDetailViewProps = {
  paymentCard: IPaymentCard;
  onClose: () => void;
};

function DetailView({ paymentCard, onClose }: TDetailViewProps) {
  const { t } = useTranslation();

  const { user } = useProfileContext();

  const hasCardSentToParent = paymentCard?.sendToParent === 1 && Boolean(user.parentUserId);

  const cardRestrictions = useMemo(() => {
    const restrictions: string[] = [];
    if (paymentCard?.merchantRestrictionGroupId) {
      restrictions.push('MID');
    }
    if (paymentCard?.mccRestrictionGroupId) {
      restrictions.push('MCC');
    }
    if (paymentCard?.countryRestrictionGroupId) {
      restrictions.push('COUNTRIES');
    }
    return restrictions;
  }, [paymentCard]);

  const isRenewalBlockHidden = !paymentCard.renewalType && !paymentCard.renewalDate && !paymentCard.originalCardId;

  return (
    <Container isScrollable={!isRenewalBlockHidden}>
      <HeaderContainer>
        <div className="flex items-center gap-3">
          <h6 className="typo-h6-bold">{t(`pages.currentProfiles.cards.settings.options.details.title`)}</h6>
          <PaymentCardStatus paymentCard={paymentCard} />
        </div>
        <IconButton icon="close" onClick={onClose} />
      </HeaderContainer>
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-x-5 gap-y-3">
          <InformationBlock
            title={t('pages.currentProfiles.cards.card.cardId')}
            value={paymentCard.cardId.toString()}
          />
          <InformationBlock title={t('pages.currentProfiles.cards.card.publicToken')} value={paymentCard.publicToken} />
          {paymentCard.pinTryExceeds > 0 && (
            <InformationBlock
              title={t('pages.currentProfiles.cards.card.pinTryExceeded')}
              value={t('pages.currentProfiles.cards.card.tryExceeded')}
              variant="error"
            />
          )}
          {cardRestrictions.length > 0 && (
            <InformationBlock
              title={t('pages.currentProfiles.cards.card.restrictions')}
              value={cardRestrictions.join(', ')}
            />
          )}
          <InformationBlock
            title={t('pages.currentProfiles.cards.card.limits')}
            value={t('pages.currentProfiles.cards.card.activated')}
            variant="success"
          />
          <InformationBlock
            title={t('pages.currentProfiles.cards.card.walletId')}
            value={paymentCard.walletId.toString()}
          />
        </div>

        <FullWidthContent>
          <Tooltip messageContent={paymentCard.cardTag || '-'} contentClassName="w-[120px]">
            <span>
              <InformationBlock
                title={t('pages.currentProfiles.cards.card.cardTag')}
                value={Utils.truncateLabel(paymentCard.cardTag || '-', 40)}
              />
            </span>
          </Tooltip>
        </FullWidthContent>

        {hasCardSentToParent && (
          <FullWidthContent>
            <SendToParent parentUserId={user.parentUserId} />
          </FullWidthContent>
        )}

        <div className="grid grid-cols-2 gap-x-5">
          <InformationBlock
            title={t('pages.currentProfiles.cards.card.createdAt')}
            value={DateTools.format(paymentCard.createdDate) || '-'}
          />
          <InformationBlock
            title={t('pages.currentProfiles.cards.card.updateDate')}
            value={DateTools.format(paymentCard.modifiedDate) || '-'}
          />
        </div>

        {isRenewalBlockHidden && (
          <FullWidthContent>
            <InformationBlock
              title={t('pages.currentProfiles.cards.card.expriryAt')}
              value={DateTools.format(paymentCard.expiryDate) || '-'}
            />
          </FullWidthContent>
        )}
      </div>
      {!isRenewalBlockHidden && <RenewalBlock paymentCard={paymentCard} onClose={onClose} />}
    </Container>
  );
}

type TPaymentDetailsProps = {
  onClose: () => void;
};

export function PaymentCardDetails({ onClose }: TPaymentDetailsProps) {
  const {
    user: { userId },
  } = useProfileContext();

  const { cardId } = useParams();

  const paymentCard = useAppSelector(
    (state) => selectProfileCardByUserIdAndCardId(state, userId, Number(cardId))?.state?.card,
  );

  if (!paymentCard) return null;

  return <DetailView paymentCard={paymentCard} onClose={onClose} />;
}
